export type ShareResourceType = 'slap' | 'sticker';
export type ShareImageFormat = 'og' | 'fc' | 'sq';

const SITE_FALLBACK = 'http://localhost:3000';

export function sanitizeEnvUrl(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  const trimmed = value
    .trim()
    .replace(/^['"`\s]+/, '')
    .replace(/['"`\s]+$/, '')
    .replace(/[)\]}>,;]+$/, '');
  return trimmed || null;
}

export function normalizeBaseUrl(url: string): string {
  const sanitized = sanitizeEnvUrl(url) ?? url;
  return sanitized.replace(/\/+$/, '');
}

export function getSiteUrl(): string {
  const raw = process.env.NEXT_PUBLIC_SITE_URL || SITE_FALLBACK;
  return normalizeBaseUrl(raw);
}

export function buildViewPath(resourceType: ShareResourceType, id: number): string {
  return `/view/${resourceType}/${id}`;
}

export function buildViewUrl(options: {
  resourceType: ShareResourceType;
  id: number;
  siteUrl?: string;
}): string {
  const path = buildViewPath(options.resourceType, options.id);
  if (!options.siteUrl) {
    return path;
  }
  return `${normalizeBaseUrl(options.siteUrl)}${path}`;
}

export function buildMiniAppPath(resourceType: ShareResourceType, id: number): string {
  return `/i/farcaster/mini/${resourceType}/${id}`;
}

export function buildMiniAppUrl(options: {
  resourceType: ShareResourceType;
  id: number;
  siteUrl?: string;
}): string {
  const path = buildMiniAppPath(options.resourceType, options.id);
  if (!options.siteUrl) {
    return path;
  }
  return `${normalizeBaseUrl(options.siteUrl)}${path}`;
}

function normalizeBaseDomain(domain?: string | null): string | null {
  const sanitized = sanitizeEnvUrl(domain);
  if (!sanitized) {
    return null;
  }
  const trimmed = sanitized;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return normalizeBaseUrl(trimmed);
  }
  return `https://${trimmed}`;
}

export function buildLocateUrl(options: {
  resourceType: ShareResourceType;
  resourceId: number;
  format: ShareImageFormat;
  chainId?: string | number | null;
  cloudfrontDomain?: string | null;
  previewBaseUrl?: string | null;
}): string | null {
  const chainId = options.chainId ?? process.env.NEXT_PUBLIC_CHAIN_ID;
  if (!chainId) {
    return null;
  }
  const cloudfrontBase = normalizeBaseDomain(
    options.cloudfrontDomain ?? process.env.NEXT_PUBLIC_CLOUDFRONT_DOMAIN
  );
  const previewBase = normalizeBaseDomain(
    options.previewBaseUrl ?? process.env.NEXT_PUBLIC_SLAP_PREVIEW_BASE_URL
  );
  const baseUrl = cloudfrontBase || previewBase;
  if (!baseUrl) {
    return null;
  }
  return `${baseUrl}/chain/${chainId}/locate/${options.resourceType}/${options.resourceId}/${options.format}`;
}

export function buildMiniAppEmbed(options: {
  imageUrl: string;
  actionUrl: string;
  buttonTitle?: string;
  appName?: string;
  splashImageUrl?: string;
  splashBackgroundColor?: string;
}): string {
  const buttonTitle = options.buttonTitle ?? 'Open StickerChain';
  const appName = options.appName ?? 'StickerChain';
  const embed = {
    version: '1',
    imageUrl: options.imageUrl,
    button: {
      title: buttonTitle,
      action: {
        type: 'launch_frame',
        name: appName,
        url: options.actionUrl,
        ...(options.splashImageUrl ? { splashImageUrl: options.splashImageUrl } : {}),
        ...(options.splashBackgroundColor
          ? { splashBackgroundColor: options.splashBackgroundColor }
          : {}),
      },
    },
  };
  return JSON.stringify(embed);
}
